import { useTareasPendientes } from './TareasPendientes';
import { useState, type ReactNode } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { ROL_ETIQUETA } from '../lib/formato';
import { useSesion } from '../lib/sesion';
import type { Rol } from '../lib/tipos';
import { Marca } from './Marca';
import { Boton } from './ui';

interface Entrada {
  a: string;
  texto: string;
  roles: Rol[];
  grupo?: string;
}

const MENU: Entrada[] = [
  { a: '/', texto: 'Panel', roles: ['SALESPERSON', 'SALES_MANAGER', 'COOK', 'ADMIN'] },
  { a: '/solicitudes', texto: 'Solicitudes', roles: ['SALESPERSON', 'SALES_MANAGER', 'COOK', 'ADMIN'] },
  { a: '/agenda', texto: 'Agenda', roles: ['SALESPERSON', 'SALES_MANAGER', 'COOK', 'ADMIN'] },
  { a: '/cocina', texto: 'Cocina', roles: ['COOK', 'SALES_MANAGER', 'ADMIN'] },
  { a: '/indisponibilidad', texto: 'Bloqueos de cocina', roles: ['COOK', 'SALES_MANAGER', 'ADMIN'] },
  { a: '/clientes', texto: 'Clientes', roles: ['SALESPERSON', 'SALES_MANAGER', 'ADMIN'] },
  { a: '/admin/importacion', texto: 'Importar clientes', roles: ['ADMIN'], grupo: 'Administración' },
  { a: '/admin/estadisticas', texto: 'Estadísticas', roles: ['ADMIN'] },
  { a: '/admin/comerciales', texto: 'Comerciales', roles: ['ADMIN'] },
  { a: '/admin/usuarios', texto: 'Usuarios', roles: ['ADMIN'] },
  { a: '/admin/configuracion', texto: 'Configuración', roles: ['ADMIN'] },
  { a: '/admin/integraciones', texto: 'Incidencias', roles: ['ADMIN'] },
  { a: '/admin/auditoria', texto: 'Auditoría', roles: ['ADMIN'] },
];

/** Secciones que salen en la barra inferior del móvil, por orden de prioridad. */
const PRIMARIAS = ['/', '/solicitudes', '/agenda', '/clientes', '/cocina'];

/* ─────────────────────────── Iconos (trazo) ─────────────────────────── */

const TRAZOS: Record<string, ReactNode> = {
  '/': <><path d="M4 12 12 4l8 8" /><path d="M6 10v10h12V10" /></>,
  '/solicitudes': <><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 8h6M9 12h6M9 16h4" /></>,
  '/agenda': <><rect x="4" y="5" width="16" height="16" rx="2" /><path d="M4 9h16M8 3v4M16 3v4" /></>,
  '/cocina': <><path d="M7 21h10M8 21v-4M16 21v-4" /><path d="M6 8a4 4 0 0 1 8-1 3.5 3.5 0 0 1 4 3.5c0 2-1.7 3.5-4 3.5H8c-2 0-3.5-1.4-3.5-3.2A3.2 3.2 0 0 1 6 8Z" /></>,
  '/indisponibilidad': <><circle cx="12" cy="12" r="9" /><path d="M5.6 5.6 18.4 18.4" /></>,
  '/clientes': <><circle cx="9" cy="8" r="3" /><path d="M3.5 20c0-3 2.5-5 5.5-5s5.5 2 5.5 5" /><path d="M16 6a3 3 0 0 1 0 6" /></>,
  '/admin/importacion': <><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" /></>,
  '/admin/comerciales': <><circle cx="9" cy="8" r="3" /><path d="M3.5 20c0-3 2.5-5 5.5-5s5.5 2 5.5 5" /></>,
  '/admin/usuarios': <><circle cx="9" cy="8" r="3" /><path d="M3.5 20c0-3 2.5-5 5.5-5s5.5 2 5.5 5" /><path d="M16 6a3 3 0 0 1 0 6" /></>,
  '/admin/configuracion': <><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" /></>,
  '/admin/integraciones': <><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></>,
  '/admin/auditoria': <><path d="M4 4h16v16H4z" /><path d="M8 9h8M8 13h5" /></>,
  nueva: <path d="M12 5v14M5 12h14" />,
  mas: <><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></>,
  cuenta: <><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.5-7 8-7s8 3 8 7" /></>,
  salir: <><path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" /><path d="M10 12H3M6 8l-4 4 4 4" /></>,
};

function Icono({ n }: { n: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {TRAZOS[n] ?? null}
    </svg>
  );
}

export function Armazon() {
  const { usuario, salir } = useSesion();
  const navegar = useNavigate();
  const tareas=useTareasPendientes();
  const contador=tareas.datos?.total??0;
  const [sabana, setSabana] = useState(false);

  if (!usuario) return null;

  const visibles = MENU.filter((e) => e.roles.includes(usuario.role));
  const puedeCrear = usuario.role === 'SALESPERSON' || usuario.role === 'ADMIN';
  const iniciales = usuario.name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const primarias = PRIMARIAS.map((a) => visibles.find((e) => e.a === a)).filter(
    (e): e is Entrada => Boolean(e),
  );
  const izq = primarias.slice(0, 2);
  const der = primarias.slice(2, puedeCrear ? 3 : 4);

  async function cerrarSesion() {
    setSabana(false);
    await salir();
    navegar('/entrar');
  }

  return (
    <div className="shell">
      <a className="saltar" href="#principal">
        Saltar al contenido
      </a>

      {/* Riel lateral — tablet y escritorio */}
      <aside className="rail">
        <div className="rail-marca">
          <Marca sobreOscuro />
        </div>

        <nav aria-label="Secciones">
          {visibles.map((e) => (
            <div key={e.a} style={{ display: 'contents' }}>
              {e.grupo && <div className="grupo eyebrow">{e.grupo}</div>}
              <NavLink to={e.a} end={e.a === '/'}>
                <span className="ic">
                  <Icono n={e.a} />
                </span>
                {e.texto}{e.a==='/'&&usuario.role==='SALESPERSON'&&<span aria-label={`${contador} tareas pendientes`}> ({contador})</span>}
              </NavLink>
            </div>
          ))}
        </nav>

        <div className="rail-pie">
          <div className="quien">{usuario.name}</div>
          <div className="rol">
            {ROL_ETIQUETA[usuario.role]}
            {usuario.salespersonExternalId ? ` · Id. ${usuario.salespersonExternalId}` : ''}
          </div>
          <div className="fila" style={{ marginTop: 10 }}>
            <Boton variante="plano" tam="s" onClick={() => navegar('/cuenta')}>
              Mi cuenta
            </Boton>
            <Boton variante="plano" tam="s" onClick={cerrarSesion}>
              Salir
            </Boton>
          </div>
        </div>
      </aside>

      {/* Barra superior — solo móvil */}
      <header className="appbar">
        <Marca sobreOscuro />
        <button className="appbar-cuenta" aria-label="Menú y cuenta" onClick={() => setSabana(true)}>
          {iniciales}
        </button>
      </header>

      <main className="contenido" id="principal">
        <Outlet />
      </main>

      {/* Barra inferior — solo móvil */}
      <nav className="tabbar" aria-label="Navegación principal">
        {izq.map((e) => (
          <NavLink key={e.a} to={e.a} end={e.a === '/'} className="tab">
            <Icono n={e.a} />
            <span>{e.texto.split(' ')[0]}{e.a==='/'&&usuario.role==='SALESPERSON'?` (${contador})`:''}</span>
          </NavLink>
        ))}
        {puedeCrear && (
          <NavLink to="/solicitudes/nueva" className="tab mas-fab">
            <span className="fab">
              <Icono n="nueva" />
            </span>
            <span>Nueva</span>
          </NavLink>
        )}
        {der.map((e) => (
          <NavLink key={e.a} to={e.a} end={e.a === '/'} className="tab">
            <Icono n={e.a} />
            <span>{e.texto.split(' ')[0]}{e.a==='/'&&usuario.role==='SALESPERSON'?` (${contador})`:''}</span>
          </NavLink>
        ))}
        <button className="tab" onClick={() => setSabana(true)}>
          <Icono n="mas" />
          <span>Más</span>
        </button>
      </nav>

      {/* Hoja "Más" — móvil */}
      {sabana && (
        <div className="velo hoja-velo" onMouseDown={(e) => e.target === e.currentTarget && setSabana(false)}>
          <div className="hoja" role="dialog" aria-modal="true" aria-label="Menú">
            <div className="hoja-tirador" />
            <div className="hoja-perfil">
              <div className="avatar-g">{iniciales}</div>
              <div>
                <div className="quien">{usuario.name}</div>
                <div className="sutil">
                  {ROL_ETIQUETA[usuario.role]}
                  {usuario.salespersonExternalId ? ` · Id. ${usuario.salespersonExternalId}` : ''}
                </div>
              </div>
            </div>
            <nav className="hoja-menu">
              {visibles.map((e) => (
                <NavLink key={e.a} to={e.a} end={e.a === '/'} onClick={() => setSabana(false)}>
                  <span className="ic">
                    <Icono n={e.a} />
                  </span>
                  {e.texto}{e.a==='/'&&usuario.role==='SALESPERSON'&&<span aria-label={`${contador} tareas pendientes`}> ({contador})</span>}
                </NavLink>
              ))}
            </nav>
            <div className="hoja-acciones">
              <button
                onClick={() => {
                  setSabana(false);
                  navegar('/cuenta');
                }}
              >
                <span className="ic">
                  <Icono n="cuenta" />
                </span>
                Mi cuenta
              </button>
              <button onClick={cerrarSesion}>
                <span className="ic">
                  <Icono n="salir" />
                </span>
                Salir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function CabeceraPagina({
  titulo,
  descripcion,
  acciones,
}: {
  titulo: string;
  descripcion?: string;
  acciones?: React.ReactNode;
}) {
  return (
    <div className="cabecera-pagina">
      <div>
        <h1>{titulo}</h1>
        {descripcion && <p>{descripcion}</p>}
      </div>
      {acciones && <div className="fila">{acciones}</div>}
    </div>
  );
}
